import React, { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import { DTOProfileCard as ProfileCard } from 'src/apis/dto/DTOProfileCard';
import IcShieldReportFill from 'src/assets/images/icShieldReportFill.svg';
import Gap from 'src/components/Gap';
import Image from 'src/components/Image';
import ChangeNicknameButton from 'src/components/ProfileModal/Full/ChangeNicknameButton';
import { useReportAndBlock } from 'src/components/ProfileModal/Full/useReportAndBlock';
import QualityUserBadgeBase from 'src/components/QualityUserBadge';
import { ModalContentContext } from 'src/context/ModalContext/ModalContentContext';
import { GENDER } from 'src/types/register';
import { hexToRgb } from 'src/utils/common';
import ProfileImage from './Image';
const TopDim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: ${({
  theme
}) => `linear-gradient(180deg, rgba(${hexToRgb(theme.color.contentDeepFixed)}, 0.5) 4.5%, rgba(${hexToRgb(theme.color.contentDeepFixed)}, 0) 100%)`};
  z-index: 1;
`;
const ProfileInfo = styled.div<{
  isTop: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: ${({
  isTop
}) => isTop ? 1 : 0};
  position: absolute;
  padding: 10px 20px 45px;
  transition: opacity 0.15s;
  border-radius: 0px 0px 16px 16px;
  bottom: 0px;
  width: 100%;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.5) 4.5%,
    rgba(0, 0, 0, 0) 100%,
    rgba(0, 0, 0, 0) 100%
  );
`;
const ProfileInfoName = styled.div`
  color: ${({
  theme
}) => theme.color.contentBrightFixed};

  /* Bold/Bold 26 */
  font-size: 26px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  word-break: break-all;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
const ProfileInfoInner = styled.div`
  display: inline-block;
`;
const QualityUserBadge = styled(QualityUserBadgeBase)`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
`;
const Country = styled.div`
  display: inline-block;
  vertical-align: middle;
`;
const CountryIcon = styled(Image)`
  width: 14px;
  vertical-align: middle;
  display: inline-block;
`;
const CountryCode = styled.span`
  display: inline-block;
  color: ${({
  theme
}) => theme.color.contentBrightFixed};
  display: inline-block;
  vertical-align: middle;

  /* Regular/Regular 12 */
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
`;
const InlineGap = styled(Gap)`
  display: inline-block;
  vertical-align: middle;
`;
const GenderWrap = styled.div`
  display: inline-block;
  vertical-align: middle;
`;
const GenderIcon = styled.img`
  width: 14px;
  height: 14px;
  display: inline-block;
  vertical-align: middle;
`;
const GenderText = styled.div`
  display: inline-block;
  vertical-align: middle;
  color: ${({
  theme
}) => theme.color.contentBrightFixed};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: -1px;
`;
const Bio = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  color: #ffffff;
  font-size: 16px;
  font-weight: 400;
  white-space: nowrap;
`;
const Online = styled.div`
  display: inline-block;
  vertical-align: middle;
  color: ${({
  theme
}) => theme.color.accentH2};
  font-size: 12px;
  font-weight: 400;
`;
const OnlineIcon = styled.div`
  display: inline-block;
  vertical-align: middle;
  background-color: ${({
  theme
}) => theme.color.accentH2};
  width: 8px;
  height: 8px;
  border-radius: 8px;
`;
const ProfileRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;
const BioWrap = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;
const ReportButton = styled.button`
  all: unset;
  z-index: 9999;
  position: absolute;
  top: 16px;
  inset-inline-start: 16px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${({
  theme
}) => theme.screenSize.tablet} {
    width: 24px;
    height: 24px;
  }
`;
const ReportIcon = styled(IcShieldReportFill)`
  width: 40px;
  height: 40px;
  color: ${({
  theme
}) => theme.color.contentBrightFixed};

  ${({
  theme
}) => theme.screenSize.tablet} {
    width: 24px;
    height: 24px;
  }
`;
const ProfileTop: React.FC<{
  profile: ProfileCard;
}> = ({
  profile
}) => {
  const {
    isTopReached
  } = useContext(ModalContentContext);
  const {
    t
  } = useTranslation();
  const genderText = useMemo(() => {
    if (!profile || !profile.gender) return '';
    if (profile.gender === GENDER.MALE) return t('gender_male');
    if (profile.gender === GENDER.FEMALE) return t('gender_female');
    return t(profile.gender);
  }, [profile, t]);
  const isMine = profile.type === 'MY_PROFILE';
  const {
    canReport,
    handleClickReportOrBlock
  } = useReportAndBlock({
    target: 'icon'
  });
  return <>
      {canReport && <ReportButton onClick={handleClickReportOrBlock}>
          <ReportIcon />
        </ReportButton>}
      <ProfileImage isFirst profileImageInfo={profile.profileImageInfos[0]} data-sentry-element="ProfileImage" data-sentry-source-file="index.tsx">
        <TopDim data-sentry-element="TopDim" data-sentry-source-file="index.tsx" />
        <ProfileInfo isTop={isTopReached} data-sentry-element="ProfileInfo" data-sentry-source-file="index.tsx">
          <Row data-sentry-element="Row" data-sentry-source-file="index.tsx">
            <ProfileInfoName data-sentry-element="ProfileInfoName" data-sentry-source-file="index.tsx">{profile.nickname}</ProfileInfoName>
            {!!profile?.qualityUserBadge && <>
                <Gap width={8} />
                <QualityUserBadge qualityUserBadge={profile.qualityUserBadge} showModalOnClick={true} showFallbackImage={true} isMine={isMine} />
              </>}
            {isMine && <>
                <Gap width={8} />
                <ChangeNicknameButton />
              </>}
          </Row>
          <ProfileInfoInner data-sentry-element="ProfileInfoInner" data-sentry-source-file="index.tsx">
            {profile.location && <>
                <Country>
                  <CountryIcon src={`/images/flags/icFlag${profile.location.countryCode}.png`} />
                  <InlineGap width={4} />
                  <CountryCode>{profile.location.country}</CountryCode>
                </Country>
              </>}
            {profile.gender && <>
                <InlineGap width={8} />
                <GenderWrap>
                  <GenderIcon src={`/images/icons/icGender${profile.gender}.svg`} alt='gender' />
                  <InlineGap width={4} />
                  <GenderText>{genderText}</GenderText>
                </GenderWrap>
              </>}
            {profile.asyncInfo?.online && <>
                <InlineGap width={8} />
                <Online>
                  <OnlineIcon />
                  <InlineGap width={4} />
                  Online
                </Online>
              </>}
          </ProfileInfoInner>
          <ProfileRow data-sentry-element="ProfileRow" data-sentry-source-file="index.tsx">
            <BioWrap data-sentry-element="BioWrap" data-sentry-source-file="index.tsx">
              {profile.profileBio && <Bio>{profile.profileBio}</Bio>}
              {!!profile.hashtagInfos?.length && <Bio>{profile.hashtagInfos.map(({
                hashtag
              }) => `#${hashtag} `)}</Bio>}
            </BioWrap>
          </ProfileRow>
        </ProfileInfo>
      </ProfileImage>
      {profile.profileImageInfos?.slice(1).map(profileImageInfo => <ProfileImage key={`profile-image-${profileImageInfo.profileImageUrl || profileImageInfo.profileVideoUrl}`} profileImageInfo={profileImageInfo} />)}
    </>;
};
export default ProfileTop;